import React from 'react';
import PropTypes from 'prop-types';

import { useTranslation } from 'react-i18next';
import useAppContext from '../../../context/useAppContext';
import seatSwapRequestModel from '../../../models/seatSwapRequest';
import { getAlphaIndex } from './utils';
import SwapSeatModal from './swapSeatModal';
import SwapSeatModalConfirm from './swapSeatModalConfirm';
import { ToastContext } from '@weezevent/nacre';
import { uuid } from '../../../pages/widgets/components/pages/new-widget';

export default function SwapSeat({ show = false, onClose = () => {}, onSubmit = () => {}, attendees = null }) {
    const { t } = useTranslation();
    const [{ event, organization }] = useAppContext();
    const toast = React.useContext(ToastContext);

    const [_attendees, setAttendees] = React.useState(
        Object.values(attendees).map((attendee, index) => {
            return {
                attendee: attendee,
                new_seat: null,
                index: index
            };
        })
    );
    const [attendeesSelected, setAttendeesSelected] = React.useState({});
    const [availability, setAvailability] = React.useState(null);
    const [seatOver, setSeatOver] = React.useState(null);
    const [seatsSelected, setSeatsSelected] = React.useState([]);

    const [showModalEdit, setShowModalEdit] = React.useState(show);
    const [showModalConfirm, setShowModalConfirm] = React.useState(false);
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    const _onClose = React.useCallback(() => {
        onClose();
        setIsSubmitting(false);
        setShowModalConfirm(false);
        setShowModalEdit(false);
    }, [onClose]);

    const onBackToMap = React.useCallback(() => {
        setShowModalConfirm(false);
        setShowModalEdit(true);
    }, []);

    const onGoToConfirm = React.useCallback(() => {
        setShowModalConfirm(true);
        setShowModalEdit(false);
    }, []);

    const onAvailability = React.useCallback(
        (availability, seatsSelected = [], attendeesSelected = {}) => {
            if (!availability.seats.length) {
                return setAvailability(availability);
            }

            availability.seats.forEach(seat => {
                //Initialize seats
                seat.zone_name = availability.name;
                seat.style = seat.style || {};
                seat.custom_label = '';
                delete seat.style.border;
                delete seat.style.opacity;
                if (seat.booking_state === 'IN_PROGRESS') {
                    seat.booking_state = 'AVAILABLE';
                }

                seat.style.color = availability.seat_categories[seat.category]?.color || '#CCCCCC';

                //Style seats selected
                seatsSelected.forEach((seatSelected, index) => {
                    if (seatSelected.uuid === seat.uuid) {
                        seat.custom_label = getAlphaIndex(index);
                        seat.style = { ...seat.style, border: 'black' };
                    }
                });

                _attendees.forEach(({ attendee, new_seat, index }) => {
                    //Check free seating
                    if (!attendee.booking.seat_uuid && attendee.booking.area_zone) {
                        return;
                    }

                    //Status seat 'IN_PROGRESS' is not a real status, just for internal logic display map
                    //Style Current seating
                    if (attendee.booking.seat_uuid === seat.uuid) {
                        if (!new_seat) {
                            seat.custom_label = String(index + 1);
                            seat.booking_state = 'IN_PROGRESS';
                        }

                        //render old seat, no-clickable
                        if (new_seat) {
                            seat.booking_state = 'PARTIALLY_BOOKING_IN_PROGRESS';
                        }
                    }

                    //Style New seating
                    if (new_seat && new_seat.uuid === seat.uuid) {
                        seat.custom_label = String(index + 1);
                        seat.booking_state = 'IN_PROGRESS';
                    }
                });

                //Style attendees selected
                Object.values(attendeesSelected).forEach(({ attendee, new_seat }) => {
                    //Style opacity on unvailable seat for an attendee selected
                    if (seat.booking_state.toUpperCase() !== 'AVAILABLE' || !attendee.isMatchingSeat(seat)) {
                        seat.style = { ...seat.style, opacity: '0.2' };
                    }

                    //Style Current seating
                    if (attendee.booking.seat_uuid === seat.uuid && !new_seat) {
                        seat.style = { ...seat.style, border: 'black' };
                        delete seat.style.opacity;
                    }

                    //Style New seating
                    if (new_seat && new_seat.uuid === seat.uuid) {
                        seat.style = { ...seat.style, border: 'black' };
                        delete seat.style.opacity;
                    }
                });
            });

            //For re-render map
            availability.key = uuid();

            setAvailability({ ...availability });
        },
        [_attendees]
    );

    const onSeatsSelected = React.useCallback(
        (seats, setOnly = false) => {
            if (setOnly) {
                setSeatsSelected(seats);
                return;
            }
            //Click map without attendee selected
            if (Object.values(attendeesSelected).length === 0) {
                //Keep Only seats available
                let seatsTmp = seats.filter(seat => ['AVAILABLE', 'IN_PROGRESS'].includes(seat.booking_state.toUpperCase()));
                //Max selectedSeats === attendees length | 702 limit max index (ZZ)
                seatsTmp = seatsTmp.slice(0, _attendees.length < 702 ? _attendees.length : 702); //26 + (26 * 26) => 26 (A,B,C,...) | 26 * 26 (AA,CD,...,ZZ)
                setSeatsSelected(seatsTmp);
                onAvailability(availability, seatsTmp, attendeesSelected);
                return;
            }

            //Allow click if a seat is available and has same category and contingent of attendee
            if (seats[0].booking_state.toUpperCase() === 'AVAILABLE' && Object.values(attendeesSelected)[0].attendee.isMatchingSeat(seats[0])) {
                let attendeesTmp = [..._attendees];
                setAttendees(
                    attendeesTmp.map(attendee => {
                        if (attendee.attendee.id in attendeesSelected) {
                            attendee.new_seat = seats[0];
                        }
                        return attendee;
                    })
                );
                //@Todo possible problem update state _attendees for onAvailability
                setAttendeesSelected({});
                onAvailability(availability, [], {});
            }
        },
        [availability, attendeesSelected, _attendees, onAvailability]
    );

    // Set seat hover for display infos in popover
    const onSeatOver = React.useCallback(
        seat => {
            if (seat?.uuid) {
                setSeatOver({
                    seat,
                    attendee:
                        _attendees.find(attendee => {
                            if (attendee.new_seat?.uuid === seat.uuid) {
                                return attendee;
                            }

                            if (!attendee.new_seat && attendee.attendee.booking.seat_uuid === seat.uuid) {
                                return attendee;
                            }

                            return null;
                        }) || null
                });
            }
        },
        [_attendees]
    );

    //Select attendee in swapSeatList
    const handleAttendeeList = React.useCallback(
        attendee => {
            setAttendeesSelected(currentState => {
                let newSelected = {};
                let isSelected = attendee.attendee.pk in { ...currentState };
                if (!isSelected) {
                    newSelected[attendee.attendee.pk] = attendee;
                }
                onAvailability(availability, [], newSelected);

                return newSelected;
            });
        },
        [availability, onAvailability]
    );

    const handleSubmit = React.useCallback(
        ({ change_barcode = false, send_email = false }) => {
            setIsSubmitting(true);
            seatSwapRequestModel
                .manual({
                    organization: organization,
                    event: event,
                    attendeesMap: _attendees,
                    useNew: true
                })
                .then(response => {
                    return response.swap_request.confirm({
                        organization,
                        event,
                        change_barcode: change_barcode,
                        send_email: send_email
                    });
                })
                .then(() => {
                    _onClose();
                    toast.success(t('ticket.swapSeat.toastSuccess'));
                    onSubmit();
                })
                .catch(e => {
                    console.error(e);
                    toast.error(t('ticket.swapSeat.toastError'));
                })
                .finally(() => {
                    setIsSubmitting(false);
                });
        },
        [_onClose, onSubmit, _attendees, organization, event]
    );

    return (
        <>
            <SwapSeatModal
                show={showModalEdit}
                onClose={_onClose}
                availability={availability}
                onAvailability={onAvailability}
                attendees={_attendees}
                onChangeAttendees={setAttendees}
                attendeesSelected={attendeesSelected}
                handleAttendeeList={handleAttendeeList}
                seatOver={seatOver}
                onSeatOver={onSeatOver}
                seatsSelected={seatsSelected}
                onSeatsSelected={onSeatsSelected}
                isSubmitting={isSubmitting}
                onSubmit={onGoToConfirm}
                categories={availability?.seat_categories}
            />
            <SwapSeatModalConfirm show={showModalConfirm} onClose={_onClose} onBackToMap={onBackToMap} attendees={_attendees} isSubmitting={isSubmitting} onSubmit={handleSubmit} />
        </>
    );
}

SwapSeat.propTypes = {
    show: PropTypes.bool,
    onClose: PropTypes.func,
    onSubmit: PropTypes.func,
    attendees: PropTypes.object
};
